import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { getAccessToken } from "~/v2/api/auth/authxToken";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { tradingApiWsUrl } from "~/v3-poc/api/snapshotStream";
import { debugKernel } from "~/v3-poc/kernel/debug";
import {
  createTransport,
  type KernelTransport,
} from "~/v3-poc/kernel/host/transport";
import {
  createQuoteStore,
  pickQuotes,
  type QuoteStore,
  type StoredQuote,
} from "~/v3-poc/kernel/quotes/quoteStore";

interface SnapshotStreamContextValue {
  store: QuoteStore;
  setSubscribedSymbols: (subscriberId: string, symbols: string[]) => void;
}

const SnapshotStreamContext = createContext<SnapshotStreamContextValue | null>(
  null,
);

const snapshotStreamURL = (
  accountID: string,
  product?: string,
  status?: string,
): string => {
  const params = new URLSearchParams({ account_id: accountID });
  if (product === "paper") {
    params.set("product", "paper");
  }
  if (status) {
    params.set("status", status);
  }
  return `${tradingApiWsUrl("/marketdata/snapshots/stream")}?${params}`;
};

const createAccountFrame = (
  account: { id: string; status?: string },
  product?: string,
) =>
  ({
    type: "account",
    key: `${account.id}|${product ?? ""}|${account.status ?? ""}`,
    socketUrl: snapshotStreamURL(account.id, product, account.status),
  }) as const;

const unionSymbols = (subscribers: Map<string, string[]>): string[] => {
  const union = new Set<string>();
  for (const symbols of subscribers.values()) {
    for (const symbol of symbols) {
      union.add(symbol);
    }
  }
  return [...union].sort();
};

export const SnapshotStreamProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentAccount, product } = useAccountContext();
  const [store] = useState(createQuoteStore);
  const transportRef = useRef<KernelTransport | null>(null);
  const subscribersRef = useRef(new Map<string, string[]>());
  const symbolsRef = useRef<string[]>([]);
  /**
   * This window's current account key, read at quote-receive time (not
   * captured by the subscribe closure below, which is created once per
   * mount) so a quote frame can be checked against the account this window
   * believes it is on right now, not whatever it was on when the transport
   * was created.
   */
  const currentAccountKeyRef = useRef<string | null>(null);
  /**
   * The account whose dropped quotes have already been reported, so a sustained
   * divergence warns once rather than on every flush. Reset on the first frame
   * that matches, so a later divergence is reported again.
   */
  const warnedAccountRef = useRef<string | null>(null);
  /**
   * Only so the two windows are distinguishable when quote tracing is on. A ref
   * rather than state because it is diagnostic, never reactive: as a dependency
   * of the transport effect it would put a console label in the same list that
   * decides whether to tear down the socket connection.
   */
  const windowTagRef = useRef(
    Math.random().toString(36).slice(2, 6).toUpperCase(),
  );
  const currentAccountID = currentAccount?.id;
  const currentAccountStatus = currentAccount?.status;

  // A `useLayoutEffect`, not `useEffect`: the account-guard effect below is
  // itself a layout effect so it can revoke synchronously (see the comment
  // there), and layout effects on the same mount run in definition order.
  // Were this one still passive, the account effect — which reads
  // `transportRef.current` — would run *before* this effect ever sets it,
  // silently dropping the first account frame on every mount.
  useLayoutEffect(() => {
    const transport = createTransport();
    transportRef.current = transport;

    const unsubscribe = transport.subscribe((frame) => {
      if (frame.type === "quotes") {
        // Read the ref, not a closed-over account value: this callback is
        // created once per mount (the effect only depends on `store`), but
        // the account can change many times over the window's lifetime.
        // `currentAccountKeyRef.current` is always this window's latest
        // account, so a quote whose source account has not converged with a
        // pending account switch (see the account effect below) is dropped
        // instead of applied under the wrong account's identity.
        if (frame.account !== currentAccountKeyRef.current) {
          // Once per offending account, not once per dropped frame. A flush
          // lands every `QUOTE_FLUSH_MS`, so warning per frame turns a
          // divergence that should read as one line into four lines a second
          // for as long as it lasts — which buries the warning that matters
          // under copies of itself.
          if (warnedAccountRef.current !== frame.account) {
            warnedAccountRef.current = frame.account;
            console.warn(
              "[atd] dropping quotes for an account this window is not on",
              {
                received: frame.account,
                expected: currentAccountKeyRef.current,
              },
            );
          }
          return;
        }
        warnedAccountRef.current = null;
        debugKernel("window received", {
          window: windowTagRef.current,
          account: frame.account,
          quotes: frame.quotes,
        });
        store.getState().applyQuotes(frame.quotes);
        return;
      }
      void getAccessToken()
        .then((token) => transport.post({ type: "token", token }))
        .catch(() => {
          // A failed token closes the socket, and the stream's own backoff
          // retries. Auth recovery belongs to the window, not the worker.
        });
    });

    if (symbolsRef.current.length > 0) {
      transport.post({ type: "symbols", symbols: symbolsRef.current });
    }

    const postVisibility = () =>
      transport.post({
        type: "visibility",
        visible: document.visibilityState === "visible",
      });

    // A MessagePort gives the worker no close event, so say goodbye explicitly.
    // `persisted` distinguishes a real teardown from bfcache: for bfcache the
    // page is frozen and may come back, and a `bye` would have dropped this
    // client from the worker's union for good — the restored window would render
    // a live workspace that never ticks again.
    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted) {
        return;
      }
      transport.post({ type: "bye" });
    };

    // Restored from bfcache: this window's effects never re-ran, so re-announce
    // its own state. Symbols and visibility are safe to replay from refs
    // because they are this window's own state and cannot have changed while
    // it was frozen. The account is deliberately *not* replayed here: a frozen
    // window may miss `BroadcastChannel` messages, so its cached account frame
    // is exactly the value most likely to be wrong. On `pageshow`, account
    // selection adopts readable durable state; unreadable or malformed storage
    // preserves the in-memory selection. The account effect reports that
    // resolved selection normally, while account-stamped quote rejection
    // remains the safety boundary during any temporary divergence.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      // Posted unconditionally: the worker still holds this client's
      // pre-freeze symbols (no `bye` was sent), so an empty union here must
      // still be stated, or the worker stays subscribed on our behalf.
      transport.post({ type: "symbols", symbols: symbolsRef.current });
      postVisibility();
    };

    document.addEventListener("visibilitychange", postVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    // The worker starts every client visible; a window opened in a background
    // tab is not, so send its real state immediately rather than waiting for
    // the first `visibilitychange`. This keeps the worker's view of each
    // client accurate and is the plumbing a future change (e.g. dropping a
    // hidden window's symbols from the union) would read.
    postVisibility();

    return () => {
      document.removeEventListener("visibilitychange", postVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      unsubscribe();
      transport.post({ type: "bye" });
      transport.close();
      transportRef.current = null;
    };
  }, [store]);

  // Revoke or replace the account key before queued worker frames can run.
  useLayoutEffect(() => {
    if (!currentAccountID) {
      // Losing the account has to retract this window's claim to it. The worker
      // is still on that account and still emitting its quotes, so leaving the
      // key here would keep a signed-out or revoked account's prices ticking.
      currentAccountKeyRef.current = null;
      store.getState().clearQuotes();
      return;
    }
    const frame = createAccountFrame(
      { id: currentAccountID, status: currentAccountStatus },
      product,
    );
    const switched = currentAccountKeyRef.current !== frame.key;
    currentAccountKeyRef.current = frame.key;
    if (switched) {
      // The old account's prices are wrong for the new one, and the worker's
      // `lastQuotes` are dropped on an account change, so nothing would
      // overwrite a stale entry for a symbol this account does not quote.
      store.getState().clearQuotes();
    }
    transportRef.current?.post(frame);
  }, [currentAccountID, currentAccountStatus, product, store]);

  const setSubscribedSymbols = useCallback(
    (subscriberId: string, symbols: string[]) => {
      if (symbols.length === 0) {
        subscribersRef.current.delete(subscriberId);
      } else {
        subscribersRef.current.set(subscriberId, symbols);
      }
      const next = unionSymbols(subscribersRef.current);
      symbolsRef.current = next;
      transportRef.current?.post({ type: "symbols", symbols: next });
    },
    [],
  );

  const value = useMemo<SnapshotStreamContextValue>(
    () => ({ store, setSubscribedSymbols }),
    [store, setSubscribedSymbols],
  );

  return (
    <SnapshotStreamContext.Provider value={value}>
      {children}
    </SnapshotStreamContext.Provider>
  );
};

const useSnapshotStreamContext = (): SnapshotStreamContextValue => {
  const value = useContext(SnapshotStreamContext);
  if (!value) {
    throw new Error(
      "useLiveQuotes/useSetSubscribedSymbols must be used within a SnapshotStreamProvider",
    );
  }
  return value;
};

/** Stable across quote flushes — take this when you only publish symbols. */
export const useSetSubscribedSymbols = () =>
  useSnapshotStreamContext().setSubscribedSymbols;

/** Re-renders only when one of `symbols` changes. */
export const useLiveQuotes = (
  symbols: readonly string[],
): Record<string, StoredQuote> => {
  const { store } = useSnapshotStreamContext();
  return useStore(
    store,
    useShallow((state) => pickQuotes(state.quotes, symbols)),
  );
};
